import { useState, useEffect, useRef, useCallback } from 'react';
import { signalRService } from '../services/signalr/SignalRService';
import { HubMethods, HubEvents, ChatMessage } from '../services/signalr/types';
import { useUser } from '../context/UserContext';
import { UnityPlayer } from '../types';
import { GESTURE_MAP } from '../data/gestures';
import { autoGestureForMessage } from '../data/chatGestures';
import { sendGameEmote, sendConversationFocus } from '../utils/gameEmote';
import { npcService } from '../services/api';
import type { CoachTopic } from '../services/api/npcService';
import './GameChat.css';

// WU-06 R5: NPC roster ids are always "npc_{guid}" (RoomHub.NpcIdPrefix) — the frontend
// already special-cases this prefix (private-talk target filtering); R7 reuses it for the
// chat AI tag, and this reuses it again to find room hosts for the R6 coaching chip.
const isPersonaId = (id?: string | null): boolean => !!id && id.startsWith('npc_');

interface GameChatProps {
  roomId: string;
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
  /**
   * Vicinity gate: user ids currently within earshot of the local player
   * (from Unity's proximity scanner). `null`/`undefined` = no vicinity data
   * (older Unity build, scene without a scanner) → FAIL OPEN and show all
   * messages, so chat never silently dies on a stale client.
   */
  nearbyPlayerIds?: string[] | null;
  /** Room roster (names for the private-talk target picker). */
  roomPlayers?: UnityPlayer[];
  /**
   * P1 sheet mode. When false the whole chat is hidden (mobile bare-HUD
   * default); the floating bubble in App toggles it. Desktop passes `true`
   * (docked-panel behavior unchanged). Default true = no regression.
   */
  open?: boolean;
  /** Render as a bottom sheet on mobile (adds a header close button). */
  mobileSheet?: boolean;
  /** Sheet close (mobile bubble/backdrop). */
  onClose?: () => void;
  /**
   * P1: the PlayerSheet's 💬 asks to start a scoped chat with this player.
   * When it changes to a non-null target, GameChat fires the existing
   * private-talk request handshake, then calls `onPrivateTalkConsumed`.
   */
  requestPrivateTalkWith?: { id: string; name: string } | null;
  onPrivateTalkConsumed?: () => void;
  /** Fired for each inbound (non-own) message that passes the vicinity gate — App counts unread while closed. */
  onNewMessage?: () => void;
  /** GameChat wants to be visible (incoming private-talk request / accept). */
  onRequestOpen?: () => void;
}

// RoomHub serializes ChatMessageType as its numeric value (no enum-string converter
// on the hub protocol): 0=Public 1=Private 2=System 3=Announcement 4=Whisper.
const numericTypeToString = (t: unknown): ChatMessage['type'] => {
  if (typeof t === 'number') {
    return ([ 'public', 'private', 'system', 'system', 'private' ] as const)[t] ?? 'public';
  }
  return (t as ChatMessage['type']) || 'public';
};

// Wire → UI shape: the hub sends `messageId` (not `id`) and numeric `type`.
const normalizeMessage = (raw: ChatMessage): ChatMessage => ({
  ...raw,
  id: raw.id ?? raw.messageId ?? `${raw.senderId}-${raw.timestamp}`,
  type: numericTypeToString(raw.type),
});

const newMessageId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Quick-gesture strip on the chat: one tap plays it on your character (and
// networks to the room like the emote bar). Ids are gesture-catalog ids.
const QUICK_GESTURES = ['wave', 'laughing', 'clapping', 'dance', 'blow-a-kiss', 'cheer'];

// Private-talk handshake envelopes arrive FLAT and targeted (call_* pattern).
interface PrivateTalkWire {
  type: string;
  talkId?: string;
  from?: { userId?: string; displayName?: string };
  participants?: { userId?: string; displayName?: string }[];
}

const REQUEST_TIMEOUT_MS = 45000;

export function GameChat({
  roomId,
  isMinimized = false,
  onToggleMinimize,
  nearbyPlayerIds,
  roomPlayers,
  open = true,
  mobileSheet = false,
  onClose,
  requestPrivateTalkWith,
  onPrivateTalkConsumed,
  onNewMessage,
  onRequestOpen,
}: GameChatProps) {
  const { user } = useUser();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // Live ref so the ReceiveChatMessage subscription (bound once per room)
  // always gates against the CURRENT vicinity set without resubscribing.
  const nearbyRef = useRef<string[] | null>(nearbyPlayerIds ?? null);
  useEffect(() => {
    nearbyRef.current = nearbyPlayerIds ?? null;
  }, [nearbyPlayerIds]);
  const [inputValue, setInputValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(!isMinimized);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // --- Private talk state (consent handshake over SocialEvent, call_* pattern) ---
  const [privateTalk, setPrivateTalk] = useState<{ talkId: string; partnerId: string; partnerName: string } | null>(null);
  const [incomingRequest, setIncomingRequest] = useState<{ talkId: string; fromId: string; fromName: string } | null>(null);
  const [outgoingPending, setOutgoingPending] = useState<{ talkId: string; toName: string } | null>(null);
  const [showTargetPicker, setShowTargetPicker] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const outgoingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Live ref mirror for the socket handler (bound once per room/user).
  const privateTalkRef = useRef(privateTalk);
  useEffect(() => { privateTalkRef.current = privateTalk; }, [privateTalk]);

  // Prop callbacks used inside once-bound socket handlers — mirrored through
  // refs so changing them never forces a resubscribe (P1).
  const onNewMessageRef = useRef(onNewMessage);
  const onRequestOpenRef = useRef(onRequestOpen);
  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
    onRequestOpenRef.current = onRequestOpen;
  });

  // --- WU-06 R6: opt-in coaching thread ("wing-tip") — tap a persona chip to open a
  // private thread with that room host. Never opened by anything but a user tap; the
  // panel itself starts empty (no auto-greeting) so the FIRST persona line only ever
  // appears after the user picks a topic or sends text — "never unsolicited" by construction.
  const [coachTarget, setCoachTarget] = useState<{ id: string; name: string } | null>(null);
  const [coachMessages, setCoachMessages] = useState<{ from: 'user' | 'persona'; content: string }[]>([]);
  const [coachInput, setCoachInput] = useState('');
  const [coachLoading, setCoachLoading] = useState(false);

  const openCoachThread = useCallback((p: UnityPlayer) => {
    setCoachTarget({ id: p.playerId, name: p.playerName });
    setCoachMessages([]);
    setCoachInput('');
  }, []);

  const closeCoachThread = useCallback(() => {
    setCoachTarget(null);
    setCoachMessages([]);
    setCoachInput('');
  }, []);

  const askCoach = useCallback(async (topic: CoachTopic, text: string) => {
    const target = coachTarget;
    const question = text.trim();
    if (!target || !question || coachLoading) return;
    setCoachMessages(prev => [...prev, { from: 'user', content: question }]);
    setCoachInput('');
    setCoachLoading(true);
    try {
      const reply = await npcService.coachWithNpc(target.id, topic, roomId);
      setCoachMessages(prev => [...prev, { from: 'persona', content: reply || "Sorry, nothing comes to mind right now." }]);
    } catch (e) {
      console.error('Coaching request failed:', e);
      setCoachMessages(prev => [...prev, { from: 'persona', content: "Sorry, can't chat right now." }]);
    } finally {
      setCoachLoading(false);
    }
  }, [coachTarget, coachLoading, roomId]);

  const myId = user?.id != null ? String(user.id) : '';

  const showNote = useCallback((text: string) => {
    setNote(text);
    setTimeout(() => setNote(null), 4000);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    setIsExpanded(!isMinimized);
  }, [isMinimized]);

  const appendSystemLine = useCallback((content: string) => {
    setMessages(prev => [...prev, {
      id: newMessageId(),
      senderId: 'system',
      senderName: '',
      content,
      type: 'system',
      channelId: roomId,
      timestamp: new Date().toISOString(),
    }]);
  }, [roomId]);

  // Persisted history on join/rejoin. Merged (not replaced) so a live broadcast
  // that races the fetch is kept; ids dedupe the overlap.
  useEffect(() => {
    let cancelled = false;
    setMessages([]);
    (async () => {
      try {
        const json = await signalRService.invoke<string>(
          HubMethods.GetRoomChatHistory, roomId, 50,
        );
        if (cancelled || !json) return;
        const history = (JSON.parse(json) as ChatMessage[]).map(normalizeMessage);
        setMessages(prev => {
          const merged = [...history];
          for (const m of prev) {
            if (!merged.some(x => x.id === m.id)) merged.push(m);
          }
          return merged;
        });
      } catch (e) {
        // Older server without the method — start with an empty log.
        console.warn('Chat history unavailable:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [roomId]);

  // Subscribe to chat messages
  useEffect(() => {
    const unsubscribe = signalRService.on<[ChatMessage]>(
      HubEvents.ReceiveChatMessage,
      (raw) => {
        const message = normalizeMessage(raw);
        // Only show messages for this room; drop duplicates (our own optimistic
        // echo comes back via the room-group broadcast with the same messageId).
        if (message.channelId !== roomId && message.channelId) return;

        // Vicinity rule: public chatter is heard only within earshot. System
        // lines, private deliveries and own messages always pass; no vicinity
        // data = fail open.
        const nearby = nearbyRef.current;
        if (
          nearby !== null &&
          message.type === 'public' &&
          message.senderId !== user?.id &&
          !nearby.includes(String(message.senderId))
        ) {
          return;
        }

        setMessages(prev => {
          if (prev.some(m => m.id === message.id)) return prev;
          // Unread signal for the floating bubble: inbound, non-own, non-system.
          if (message.senderId !== user?.id && message.type !== 'system') {
            onNewMessageRef.current?.();
          }
          return [...prev, message];
        });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [roomId, user?.id]);

  // --- Private talk: envelope send helper (stringified outer envelope law) ---
  const sendEnvelope = useCallback(
    async (type: string, payload: Record<string, unknown>): Promise<{ ok: boolean; [k: string]: unknown }> => {
      try {
        const envelopeJson = JSON.stringify({ type, payloadJson: JSON.stringify(payload) });
        const json = await signalRService.invoke<string>(HubMethods.SocialEvent, roomId, envelopeJson);
        return typeof json === 'string' ? JSON.parse(json) : (json as { ok: boolean });
      } catch (e) {
        console.error(`[PrivateTalk] ${type} failed:`, e);
        return { ok: false };
      }
    },
    [roomId]
  );

  const clearOutgoing = useCallback(() => {
    if (outgoingTimerRef.current) clearTimeout(outgoingTimerRef.current);
    outgoingTimerRef.current = null;
    setOutgoingPending(null);
  }, []);

  const endPrivateState = useCallback((reason?: string) => {
    setPrivateTalk(null);
    sendConversationFocus(null);
    if (reason) appendSystemLine(reason);
  }, [appendSystemLine]);

  // --- Private talk: incoming flat envelopes (targeted — receipt = it's for us) ---
  useEffect(() => {
    if (!myId) return;
    const unsubscribe = signalRService.on<[string]>(
      HubEvents.SocialEventReceived,
      (raw) => {
        let envelope: PrivateTalkWire;
        try {
          envelope = typeof raw === 'string' ? JSON.parse(raw) : (raw as PrivateTalkWire);
        } catch {
          return;
        }
        if (!envelope?.type?.startsWith('private_talk')) return;

        switch (envelope.type) {
          case 'private_talk_request': {
            if (!envelope.talkId || !envelope.from?.userId) return;
            setIncomingRequest({
              talkId: envelope.talkId,
              fromId: envelope.from.userId,
              fromName: envelope.from.displayName || 'Someone',
            });
            onRequestOpenRef.current?.(); // surface the Accept/Decline banner
            break;
          }
          case 'private_talk_accept': {
            const partner = envelope.participants?.find(p => p.userId && p.userId !== myId);
            if (!envelope.talkId || !partner?.userId) return;
            clearOutgoing();
            setIncomingRequest(null);
            setPrivateTalk({
              talkId: envelope.talkId,
              partnerId: partner.userId,
              partnerName: partner.displayName || 'them',
            });
            appendSystemLine(`🔒 Private talk with ${partner.displayName || 'them'} — only you two can read this.`);
            onRequestOpenRef.current?.(); // ensure the chat sheet is visible for the talk
            // Swing the camera side-on to frame both of you.
            sendConversationFocus(partner.userId);
            break;
          }
          case 'private_talk_decline': {
            clearOutgoing();
            showNote('Not available for a private talk right now');
            break;
          }
          case 'private_talk_end': {
            if (privateTalkRef.current && envelope.talkId === privateTalkRef.current.talkId) {
              endPrivateState('🔓 Private talk ended.');
            }
            clearOutgoing();
            break;
          }
        }
      }
    );
    return () => { unsubscribe(); };
  }, [myId, appendSystemLine, clearOutgoing, endPrivateState, showNote]);

  // Leaving the room mid-talk: release the camera (Unity side also despawns).
  useEffect(() => () => { sendConversationFocus(null); }, [roomId]);

  const requestPrivateTalk = useCallback(async (target: { id: string; name: string }) => {
    setShowTargetPicker(false);
    const resp = await sendEnvelope('private_talk_request', { targetUserId: target.id });
    if (resp.ok && typeof resp.talkId === 'string') {
      setOutgoingPending({ talkId: resp.talkId, toName: target.name });
      outgoingTimerRef.current = setTimeout(() => {
        setOutgoingPending(null);
        showNote('No answer');
      }, REQUEST_TIMEOUT_MS);
    } else {
      showNote("They're unavailable right now");
    }
  }, [sendEnvelope, showNote]);

  // P1: the PlayerSheet's 💬 "Chat" asks to open a scoped talk with one player.
  // Fire the shipped consent handshake (or no-op if already talking to them),
  // then consume so the request runs once per target selection.
  const lastScopeRequestRef = useRef<string | null>(null);
  useEffect(() => {
    const target = requestPrivateTalkWith;
    if (!target) { lastScopeRequestRef.current = null; return; }
    if (lastScopeRequestRef.current === target.id) return;
    lastScopeRequestRef.current = target.id;

    onRequestOpenRef.current?.();
    if (privateTalk?.partnerId === target.id) {
      // already in this talk — just show it
    } else if (!privateTalk && !outgoingPending) {
      void requestPrivateTalk({ id: target.id, name: target.name });
    } else {
      showNote(`Finish your current talk before starting one with ${target.name}`);
    }
    onPrivateTalkConsumed?.();
  }, [requestPrivateTalkWith, privateTalk, outgoingPending, requestPrivateTalk, showNote, onPrivateTalkConsumed]);

  const respondToRequest = async (accept: boolean) => {
    if (!incomingRequest) return;
    const req = incomingRequest;
    setIncomingRequest(null);
    await sendEnvelope(accept ? 'private_talk_accept' : 'private_talk_decline', { talkId: req.talkId });
    // The accept state lands via our own private_talk_accept envelope (server echo).
  };

  const endPrivateTalk = async () => {
    if (!privateTalk) return;
    const talkId = privateTalk.talkId;
    endPrivateState('🔓 Private talk ended.');
    await sendEnvelope('private_talk_end', { talkId });
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || !user) return;

    const messageId = newMessageId();
    const content = inputValue.trim();
    const isPrivate = !!privateTalk;
    try {
      // `type` must be the numeric enum value — the hub cannot bind the string
      // form ('public') into C# ChatMessageType, which made every send fail.
      // Private talks ride the hub's Private type: delivered ONLY to the
      // recipients list (server-enforced), never the room group.
      await signalRService.invoke(HubMethods.SendChatMessage, {
        messageId,
        content,
        channelId: roomId,
        type: isPrivate ? 1 : 0,
        recipients: isPrivate ? [privateTalk.partnerId] : undefined,
      });

      // Optimistically add our own message; the server echo dedupes by messageId.
      const newMessage: ChatMessage = {
        id: messageId,
        senderId: user.id,
        senderName: user.displayName,
        content,
        type: isPrivate ? 'private' : 'public',
        channelId: roomId,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev =>
        prev.some(m => m.id === newMessage.id) ? prev : [...prev, newMessage]);
      setInputValue('');

      // Chat-intent auto-gesture: saying "haha"/"bravo"/"hi" makes your
      // character act it out (rate-limited; networks like any emote).
      const gesture = autoGestureForMessage(content, 'self');
      if (gesture) sendGameEmote(gesture);
    } catch (e) {
      console.error('Failed to send message:', e);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
    onToggleMinimize?.();
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Private-talk candidates: real remote humans in the roster, earshot first.
  const targetCandidates = (roomPlayers ?? [])
    .filter(p => !p.isLocal && p.playerId !== myId && !isPersonaId(p.playerId))
    .sort((a, b) => {
      const an = nearbyPlayerIds?.includes(a.playerId) ? 0 : 1;
      const bn = nearbyPlayerIds?.includes(b.playerId) ? 0 : 1;
      return an - bn;
    });

  // WU-06 R6: persona room hosts present, for the "ask for a tip" chip row.
  const personaHosts = (roomPlayers ?? []).filter(p => isPersonaId(p.playerId));

  return (
    <div
      className={`game-chat ${isExpanded ? 'expanded' : 'minimized'} ${privateTalk ? 'private-active' : ''} ${mobileSheet ? 'game-chat--sheet' : ''} ${open ? '' : 'game-chat--closed'}`}
    >
      <div className="chat-header" onClick={toggleExpand}>
        <span className="chat-title">
          {privateTalk ? `🔒 ${privateTalk.partnerName}` : 'Chat'}
        </span>
        {!privateTalk && nearbyPlayerIds != null && isExpanded && (
          <span
            className="chat-earshot"
            title="Room chat carries only to people near you"
          >
            {nearbyPlayerIds.length > 0
              ? `🗣 ${nearbyPlayerIds.length} in earshot`
              : '🤫 no one in earshot'}
          </span>
        )}
        {messages.length > 0 && !isExpanded && (
          <span className="unread-badge">{messages.length}</span>
        )}
        {isExpanded && (
          privateTalk ? (
            <button
              className="private-talk-btn active"
              onClick={(e) => { e.stopPropagation(); endPrivateTalk(); }}
              title="End the private talk"
            >
              🔓 End
            </button>
          ) : (
            <button
              className="private-talk-btn"
              onClick={(e) => { e.stopPropagation(); setShowTargetPicker(v => !v); }}
              title="Request a private talk — only the two of you will read the chat"
              disabled={!!outgoingPending}
            >
              {outgoingPending ? '⏳' : '🔒'}
            </button>
          )
        )}
        {mobileSheet && onClose ? (
          <button
            className="chat-sheet-close"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            aria-label="Close chat"
          >
            ×
          </button>
        ) : (
          <button className="chat-toggle" onClick={toggleExpand}>
            {isExpanded ? '−' : '+'}
          </button>
        )}
      </div>

      {isExpanded && showTargetPicker && !privateTalk && (
        <div className="private-target-picker">
          <div className="private-target-title">Private talk with…</div>
          {targetCandidates.length === 0 && (
            <div className="private-target-empty">No one else is here yet</div>
          )}
          {targetCandidates.map(p => (
            <button
              key={p.playerId}
              className="private-target-option"
              onClick={() => requestPrivateTalk({ id: p.playerId, name: p.playerName })}
            >
              {p.playerName}
              {nearbyPlayerIds?.includes(p.playerId) && <span className="private-target-near">nearby</span>}
            </button>
          ))}
        </div>
      )}

      {isExpanded && incomingRequest && (
        <div className="private-request-banner">
          <span className="private-request-text">
            🔒 <strong>{incomingRequest.fromName}</strong> requests a private talk
          </span>
          <div className="private-request-actions">
            <button className="private-accept" onClick={() => respondToRequest(true)}>Accept</button>
            <button className="private-decline" onClick={() => respondToRequest(false)}>Decline</button>
          </div>
        </div>
      )}

      {isExpanded && outgoingPending && (
        <div className="private-request-banner outgoing">
          <span className="private-request-text">⏳ Waiting for {outgoingPending.toName}…</span>
        </div>
      )}

      {isExpanded && note && <div className="chat-note">{note}</div>}

      {isExpanded && (
        <>
          <div className="chat-messages">
            {messages.length === 0 && (
              <div className="chat-empty">
                No messages yet. Say hello!
              </div>
            )}
            {messages.map((msg) => {
              const isPersona = isPersonaId(msg.senderId);
              return (
                <div
                  key={msg.id}
                  className={`chat-message ${msg.senderId === user?.id ? 'own' : ''} ${msg.type === 'system' ? 'system' : ''} ${msg.type === 'private' ? 'private' : ''} ${isPersona ? 'persona' : ''}`}
                >
                  {msg.type !== 'system' && (
                    <span className="message-sender">
                      {msg.type === 'private' && '🔒 '}{msg.senderName}
                      {/* WU-06 R7: every persona-authored line is tagged, never blended in as human. */}
                      {isPersona && <span className="ai-tag" title="AI-generated persona message">AI</span>}
                    </span>
                  )}
                  <span className="message-content">{msg.content}</span>
                  <span className="message-time">{formatTime(msg.timestamp)}</span>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* WU-06 R6: tap a persona chip to open a private, opt-in coaching thread —
              this row only ever fires in response to a user tap (openCoachThread). */}
          {!privateTalk && !coachTarget && personaHosts.length > 0 && (
            <div className="persona-chip-row" role="toolbar" aria-label="Ask a room host for a private tip">
              {personaHosts.map((p) => (
                <button
                  key={p.playerId}
                  type="button"
                  className="persona-chip"
                  title={`Ask ${p.playerName} for a private tip — only you will see it`}
                  onClick={() => openCoachThread(p)}
                >
                  ✨ {p.playerName}
                </button>
              ))}
            </div>
          )}

          {coachTarget && (
            <div className="coach-thread">
              <div className="coach-thread-header">
                <span>✨ Private tip from {coachTarget.name}</span>
                <button className="coach-thread-close" onClick={closeCoachThread} aria-label="Close coaching thread">×</button>
              </div>
              <div className="coach-thread-messages">
                {coachMessages.length === 0 && (
                  <div className="coach-thread-empty">Only you can see this. Pick a topic, or just ask.</div>
                )}
                {coachMessages.map((m, i) => (
                  <div key={i} className={`coach-thread-msg ${m.from}`}>
                    {m.from === 'persona' && <span className="ai-tag" title="AI-generated persona message">AI</span>}
                    {m.content}
                  </div>
                ))}
              </div>
              {coachMessages.length === 0 && (
                <div className="coach-thread-topics">
                  <button type="button" onClick={() => askCoach('connection-coach', 'Any icebreaker ideas?')}>
                    💬 Icebreaker ideas
                  </button>
                  <button type="button" onClick={() => askCoach('inhibition-help', "I'm feeling nervous")}>
                    🌱 Feeling nervous?
                  </button>
                </div>
              )}
              <div className="coach-thread-input">
                <input
                  type="text"
                  value={coachInput}
                  onChange={(e) => setCoachInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); void askCoach('connection-coach', coachInput); }
                  }}
                  placeholder="Ask for a tip…"
                  disabled={coachLoading}
                />
                <button
                  type="button"
                  disabled={!coachInput.trim() || coachLoading}
                  onClick={() => askCoach('connection-coach', coachInput)}
                >
                  Ask
                </button>
              </div>
            </div>
          )}

          <div className="chat-gesture-strip" role="toolbar" aria-label="Quick gestures">
            {QUICK_GESTURES.map((id) => {
              const g = GESTURE_MAP.get(id);
              if (!g) return null;
              return (
                <button
                  key={id}
                  type="button"
                  className="chat-gesture-btn"
                  title={g.label}
                  aria-label={g.label}
                  onClick={() => sendGameEmote(id)}
                >
                  {g.emoji}
                </button>
              );
            })}
          </div>

          <div className="chat-input-container">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={privateTalk ? `Private with ${privateTalk.partnerName}…` : 'Type a message...'}
              className="chat-input"
            />
            <button
              onClick={sendMessage}
              disabled={!inputValue.trim()}
              className="chat-send-btn"
            >
              Send
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default GameChat;
